/**
 * How long a generated practice should be, in the words a tutor picks from.
 *
 * A tutor asked for "a number of questions" used to be asked for a number, and
 * a number is a worse question than it looks: 30 and 32 are not a decision
 * anybody has an opinion about, and the field invited one anyway. Four sizes
 * are a decision — a warm-up, a homework set, a revision sheet, a mock — and
 * each one still shows its count, because the count is what actually happens.
 *
 * The size never leaves the browser. `GeneratePracticeInput` takes a
 * `questionCount` and always did: the sizes are this client's wording for the
 * same contract, so a second client is free to ask a different question, and
 * the server's own default and ceiling are unchanged. `questionsForSize` below
 * is where the two meet, and is the only place the numbers are written down.
 */
export const PRACTICE_SIZES = ['S', 'M', 'L', 'XL'] as const

export type PracticeSize = (typeof PRACTICE_SIZES)[number]

/**
 * The one mapping. Every count in the flow — the option a tutor reads, the
 * number the mutation carries — is read from here rather than written out
 * again, so the two can never disagree about what "L" means.
 */
const QUESTIONS: Record<PracticeSize, number> = {
  S: 25,
  M: 50,
  L: 75,
  XL: 100,
}

export function questionsForSize(size: PracticeSize): number {
  return QUESTIONS[size]
}

/** The effective count, capped by the server-published plan limit. */
export function questionsForPlan(size: PracticeSize, questionLimit: number): number {
  return Math.min(QUESTIONS[size], questionLimit)
}

/** Keep the smallest preset for each distinct effective question count. */
export function sizesForPlan(questionLimit: number): PracticeSize[] {
  const counts = new Set<number>()
  return PRACTICE_SIZES.filter((size) => {
    const count = questionsForPlan(size, questionLimit)
    if (counts.has(count)) return false
    counts.add(count)
    return true
  })
}

/**
 * Where the tab opens.
 *
 * The smallest, deliberately. It is the closest to the count this flow asked
 * for before sizes existed, and a tutor who has not thought about length gets
 * the generation that is quickest to run and cheapest to be wrong about.
 */
export const DEFAULT_PRACTICE_SIZE: PracticeSize = 'S'
